<template>
  <div>
    <b-modal
      v-show="!modalLoads"
      id="clientInfoModal"
      ref="clientinfomodal"
      cancel-variant="outline-secondary"
      ok-variant="danger"
      :ok-title="$t('DEBTIA_EDIT_INFO')"
      cancel-title="Close"
      centered
      :no-close-on-backdrop="true"
      title="Client Information"
      size="lg"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <b-overlay
        id="overlay-background"
        :show="loader === true"
        variant="dark"
        opacity="0.85"
        blur="2px"
        rounded="sm"
        spinner-variant="primary"
        spinner-medium
      >
        <b-alert
          variant="danger"
          :show="alertErrors.length > 0"
          class="mb-0"
        >
          <div
            v-for="(row, index) in alertErrors"
            :key="index"
            class="alert-body"
          >
            <feather-icon

              icon="InfoIcon"
              class="mr-50"
            />
            {{ row }}
          </div>
        </b-alert>
        <b-form>
          <validation-observer
            ref="editClientForm"
            tag="form"
          >
            <b-form-group>
              <validation-provider
                #default="{ errors }"
                name="Road Name"
                rules="required"
              >
                <label for="road_name">{{ $t('DEBTIA_ROAD_NAME') }}</label>
                <b-form-input
                  id="road_name"
                  v-model="address.road_name"
                  type="text"
                  :placeholder="$t('DEBTIA_ROAD_NAME')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <b-form-group>
              <validation-provider
                #default="{ errors }"
                name="House Number"
                rules="required"
              >
                <label for="house_no">{{ $t('DEBTIA_HOUSE_NUMBER') }}</label>
                <b-form-input
                  id="house_no"
                  v-model="address.house_no"
                  type="text"
                  :placeholder="$t('DEBTIA_HOUSE_NUMBER')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <b-form-group>
              <validation-provider
                #default="{ errors }"
                name="Zip Code"
                rules="required"
              >
                <label for="zip_code">{{ $t('DEBTIA_ZIP_CODE') }}</label>
                <b-form-input
                  id="billing-zip-code"
                  v-model="address.zip_code"
                  trim
                  :placeholder="$t('DEBTIA_ZIP_CODE')"
                  @change="getCityName($event)"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <b-form-group>
              <validation-provider
                #default="{ errors }"
                name="City"
                rules="required"
              >
                <label for="city">{{ $t('DEBTIA_CITY') }}</label>
                <b-form-input
                  id="city"
                  v-model="address.city"
                  type="text"
                  trim
                  :placeholder="$t('DEBTIA_CITY')"
                  @change="getZipCode($event)"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <b-form-group>
              <validation-provider
                #default="{ errors }"
                name="Country"
                rules="required"
              >
                <label for="country">{{ $t('DEBTIA_COUNTRY') }}</label>
                <v-select
                  v-model="address.country_id"
                  :disabled="busy"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="($i18n.locale === 'en') ? en_countries : da_countries"
                  :reduce="val => val.value"
                  :clearable="false"
                  input-id="client_billing_address_country"
                  :value="'Select Country'"
                  @input="setCountryName(address.country_id)"
                >
                  <template
                    #selected-option-container="{ option }"
                  >
                    <div class="vs__selected">
                      {{ $t(option.label) }}</div>
                  </template>
                  <template
                    slot="option"
                    slot-scope="option"
                  >
                    {{ $t(option.label) }}
                  </template>
                </v-select>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <b-form-group>
              <label for="contact_person">{{ $t('DEBTIA_CONTACT_PERSON') }}</label>
              <b-form-input
                id="contact_person"
                v-model="client.contact_person"
                type="text"
                :placeholder="$t('DEBTIA_CONTACT_PERSON')"
              />
            </b-form-group>

            <b-form-group v-if="client.type !== 'person'">
              <validation-provider
                #default="validationContext"
                name="CVR"
                rules="integer|length:8"
              >
                <label for="cvr">{{ $t('DEBTIA_CVRCPR') }}</label>
                <b-form-input
                  id="cvr"
                  v-model="client.cvrcpr"
                  type="text"
                  :state="getValidationState(validationContext)"
                  maxlength="8"
                  :placeholder="$t('DEBTIA_CVRCPR')"
                />
                <small class="text-danger">{{ validationContext.errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </validation-observer>
        </b-form>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import {
  BModal, BOverlay, BFormGroup, BForm, BFormInput, BAlert,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import axios from '@axios'
import constants from '@/app-configs/constants'
import vSelect from 'vue-select'

import formValidation from '@core/comp-functions/forms/form-validation'

export default {
  components: {
    BModal,
    BOverlay,
    BFormGroup,
    BForm,
    BFormInput,
    vSelect,
    BAlert,
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    clientData: {
      type: Object,
      required: true,
    },
    modalLoads: {
      type: Boolean,
      required: true,
    },
    clientAddress: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      address: { ...this.clientAddress },
      client: { ...this.clientData },
      busy: false,
      constants,
      modalLoader: false,
      alertErrors: [],
      zipCodes: [],
      cities: [],
      en_countries: [],
      da_countries: [],
      errorMsg: '',
      loader: false,
    }
  },
  watch: {
    clientAddress: {
      handler() {
        this.address = { ...this.clientAddress }
        this.client = { ...this.clientData }
      },
    },
  },
  created() {
    this.getCountries()
  },
  methods: {
    setCountryName(val) {
      const country = this.en_countries.find(item => item.value === val)
      this.address.country = country ? country.label : ''
    },
    async getCountries() {
      const url = `${constants.caseServerBaseUrl}debtiaapp/settings/get-countries-dropdown`
      await axios.get(url).then(response => {
        if (response.status === 200) {
          const countries = response.data.data
          this.en_countries = countries.en
          this.da_countries = countries.da
        }
      })
    },
    async getCityName(event) {
      await axios.get(`${constants.caseServerBaseUrl}debtiaapp/settings/get-city-name-by-code?code=${event}`)
        .then(response => {
          this.address.city = response.data.data.city_name !== null && response.data.data.city_name !== '' ? response.data.data.city_name : 'N/A'
        }).catch(error => {
          console.log(error)
        })
    },
    async getZipCode(event) {
      await axios.get(`${constants.caseServerBaseUrl}debtiaapp/settings/get-zip-code-by-city-name?city=${event}`)
        .then(response => {
          this.address.zip_code = response.data.data.zip_code !== null && response.data.data.zip_code !== '' ? response.data.data.zip_code : 'N/A'
        }).catch(error => {
          console.log(error)
        })
    },
    async handleSubmit(event) {
      event.preventDefault()
      this.loader = true
      this.$refs.editClientForm.validate().then(async success => {
        if (success) {
          this.$emit('update-data', { address: this.address, client: this.client })
          this.loader = false
          this.$refs.clientinfomodal.hide()
        } else {
          this.loader = false
        }
      })
    },
    resetModal() {
      this.address = { ...this.clientAddress }
      this.client = { ...this.clientData }
    },
    getValidationState(field) {
      return formValidation(this.resetModal).getValidationState(field)
    },
  },
}
</script>

<style>

</style>
